import React, { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  
  // Check localStorage on mount to determine if we should show the prompt
  const getInitialShowPrompt = () => {
    const dismissedTime = localStorage.getItem('pwaInstallDismissed');
    if (dismissedTime) {
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      return Date.now() - parseInt(dismissedTime) >= sevenDays;
    }
    return false;
  };

  const [showPrompt, setShowPrompt] = useState(() => getInitialShowPrompt());

  useEffect(() => {
    const handler = (e) => {
      const shouldPrompt = getInitialShowPrompt();
      if (!shouldPrompt) {
        return;
      }

      // Prevent the default browser prompt and show our custom install banner
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', handler);

    // Check if the app is already installed
    const checkIfInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setShowPrompt(false);
      }
    };

    checkIfInstalled();
    window.addEventListener('appinstalled', () => {
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', () => {});
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show the browser's install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond
    const { outcome } = await deferredPrompt.userChoice;

    // Clear the deferred prompt
    setDeferredPrompt(null);
    setShowPrompt(false);

    // Log the outcome
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Store in localStorage to not show again for this session
    localStorage.setItem('pwaInstallDismissed', Date.now().toString());
  };

  if (!showPrompt || !deferredPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl shadow-2xl p-4 z-50 animate-slide-up">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
            <Download className="w-6 h-6" />
          </div>
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg">Install Our App</h3>
          <p className="text-sm text-white/90 mt-1">
            Get the full experience by adding this app to your home screen.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleInstallClick}
              className="flex-1 bg-white text-emerald-600 font-semibold px-4 py-2 rounded-lg hover:bg-white/90 transition-colors text-sm"
            >
              Install Now
            </button>
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 bg-white/20 text-white px-3 py-2 rounded-lg hover:bg-white/30 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
